import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ReceiptModal } from '../components/ReceiptModal';
import { closingLabel } from '../lib/closingMethods';
import { ReturnsContent } from './ReturnsPage';
import { ProductSalesReport } from '../components/ProductSalesReport';
import { WeightSalesReport } from '../components/WeightSalesReport';
import { DiscountsReport } from '../components/DiscountsReport';
import { CreditSalesReport } from '../components/CreditSalesReport';
import { ReceiptsByPeriodReport } from '../components/ReceiptsByPeriodReport';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DateRangeFilter, downloadCsv } from '../components/DateRangeFilter';
import axios from 'axios';
import { X, ShoppingBag, Calendar, Search, Printer, Receipt, RotateCcw, Eye, Percent, Scale, Clock, HandCoins, Hash } from 'lucide-react';
import { overlayClose } from '../lib/overlayClose';
import { useListKeyboardNav } from '../lib/useListKeyboardNav';
import { NfceBadge, NfceActions, NfceContingencyBanner, NfceReadyPill } from '../components/NfceActions';
import { NfeBadge, NfeActions } from '../components/NfeActions';
import { PixBadge, PixRecheckButton, PixReconcileButton } from '../components/PixReconcile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const todayStr = () => new Date().toISOString().substring(0, 10);
const fmtBr = (iso) => (iso ? iso.split('-').reverse().join('/') : '');

export const SalesPage = () => {
  const [mainTab, setMainTab] = useState('sales');
  const [sales, setSales] = useState([]);
  const [todaySummary, setTodaySummary] = useState(null);
  const [filter, setFilter] = useState('range'); // range | cancelled
  const [dateRange, setDateRange] = useState({ start: todayStr(), end: todayStr() });
  const [searchTerm, setSearchTerm] = useState('');
  const nav = useListKeyboardNav({ count: sales.length, resetKey: sales, onSelect: (i) => setViewSaleDetail(sales[i]) });
  const [saleNumber, setSaleNumber] = useState('');
  const [saleNumberMsg, setSaleNumberMsg] = useState('');
  const [cancelling, setCancelling] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [reprintSale, setReprintSale] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewSaleDetail, setViewSaleDetail] = useState(null);

  useEffect(() => {
    loadSales();
    loadTodaySummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, dateRange]);

  const loadSales = async (term = '') => {
    try {
      const params = new URLSearchParams();
      params.set('limit', '500');
      if (term) {
        params.set('search', term);
      } else {
        params.set('start_date', dateRange.start);
        params.set('end_date', dateRange.end);
      }
      const { data } = await axios.get(`${API}/sales?${params.toString()}`, { withCredentials: true });
      const filtered = filter === 'cancelled' ? data.filter(s => s.status === 'cancelled')
        : filter === 'pix_pending' ? data.filter(s => s.pix_status === 'pendente' && s.status !== 'cancelled')
        : filter === 'pix_paid' ? data.filter(s => s.pix_status === 'pago')
        : data;
      setSales(filtered);
    } catch (err) {
      console.error('Erro:', err);
    }
  };

  const loadTodaySummary = async () => {
    try {
      const params = new URLSearchParams({ start_date: dateRange.start, end_date: dateRange.end });
      const { data } = await axios.get(`${API}/sales/today/summary?${params.toString()}`, { withCredentials: true });
      setTodaySummary(data);
    } catch (err) {
      console.error('Erro:', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadSales(searchTerm.trim());
  };

  const clearSearch = () => {
    setSearchTerm('');
    loadSales('');
  };

  // Busca direta pelo número da venda: mostra só ela na lista e abre os itens
  const handleSaleNumberSearch = async (e) => {
    e.preventDefault();
    const n = saleNumber.trim();
    if (!n) return;
    setSaleNumberMsg('');
    try {
      const { data } = await axios.get(`${API}/sales?search=${encodeURIComponent(n)}&limit=5`, { withCredentials: true });
      const exact = data.find((s) => String(s.sale_number) === n) || data[0];
      if (!exact) { setSaleNumberMsg(`Venda #${n} não encontrada.`); return; }
      setSales([exact]);
      setSearchTerm('');
      setViewSaleDetail(exact);
    } catch (err) {
      setSaleNumberMsg('Erro ao buscar venda');
    }
  };

  const handleReprint = async (saleId) => {
    try {
      const { data } = await axios.get(`${API}/sales/${saleId}`, { withCredentials: true });
      setReprintSale(data);
    } catch (err) {
      setError('Erro ao carregar venda');
    }
  };

  const handleCancel = async () => {
    if (cancelReason.length < 3) {
      setError('Informe o motivo do cancelamento (mín 3 caracteres)');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/sales/${cancelling}/cancel`, { reason: cancelReason }, { withCredentials: true });
      setCancelling(null);
      setCancelReason('');
      loadSales(searchTerm);
      loadTodaySummary();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao cancelar venda');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (cat) => ({ acougue: 'Açougue', bebidas: 'Bebidas', mercearia: 'Mercearia', outros: 'Outros' }[cat] || cat);
  const getCategoryColor = (cat) => ({
    acougue: 'bg-red-100 text-red-700 border-red-200',
    bebidas: 'bg-blue-100 text-blue-700 border-blue-200',
    mercearia: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    outros: 'bg-slate-100 text-slate-700 border-slate-200'
  }[cat] || 'bg-slate-100 text-slate-700 border-slate-200');

  return (
    <Layout>
      <div className="p-8" data-testid="sales-page">
        <h1 className="text-4xl font-black text-slate-900 tracking-tight mb-4">Vendas & Devoluções</h1>

        <Tabs value={mainTab} onValueChange={setMainTab}>
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-7 mb-6 h-auto">
            <TabsTrigger value="sales" data-testid="tab-sales-list">
              <Receipt size={16} className="mr-2" /> Vendas
            </TabsTrigger>
            <TabsTrigger value="product-report" data-testid="tab-product-report">
              <Search size={16} className="mr-2" /> Por Produto
            </TabsTrigger>
            <TabsTrigger value="weight-report" data-testid="tab-weight-report">
              <Scale size={16} className="mr-2" /> Por Peso
            </TabsTrigger>
            <TabsTrigger value="credit-report" data-testid="tab-credit-report">
              <Clock size={16} className="mr-2" /> Vendas a Prazo
            </TabsTrigger>
            <TabsTrigger value="receipts-report" data-testid="tab-receipts-report">
              <HandCoins size={16} className="mr-2" /> Recebimentos
            </TabsTrigger>
            <TabsTrigger value="discounts" data-testid="tab-discounts">
              <Percent size={16} className="mr-2" /> Descontos
            </TabsTrigger>
            <TabsTrigger value="returns" data-testid="tab-returns">
              <RotateCcw size={16} className="mr-2" /> Devoluções
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sales">
        <NfceContingencyBanner onChanged={() => loadSales()} />
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">Lista de Vendas <NfceReadyPill /></h2>
          <div className="flex gap-2">
            <Button
              onClick={() => { setFilter('range'); setSearchTerm(''); }}
              variant={filter === 'range' ? 'default' : 'outline'}
              className={filter === 'range' ? 'bg-blue-600 hover:bg-blue-700' : ''}
              data-testid="filter-range"
            >
              <Calendar size={16} className="mr-2" /> Por Período
            </Button>
            <Button
              onClick={() => { setFilter('cancelled'); setSearchTerm(''); }}
              variant={filter === 'cancelled' ? 'default' : 'outline'}
              className={filter === 'cancelled' ? 'bg-red-600 hover:bg-red-700' : 'border-red-500 text-red-600'}
              data-testid="filter-cancelled"
            >
              <X size={16} className="mr-2" /> Canceladas
            </Button>
            <Button
              onClick={() => { setFilter('pix_pending'); setSearchTerm(''); }}
              variant={filter === 'pix_pending' ? 'default' : 'outline'}
              className={filter === 'pix_pending' ? 'bg-amber-500 hover:bg-amber-600 text-slate-900' : 'border-amber-500 text-amber-800'}
              data-testid="filter-pix-pending"
            >
              PIX pendente
            </Button>
            <Button
              onClick={() => { setFilter('pix_paid'); setSearchTerm(''); }}
              variant={filter === 'pix_paid' ? 'default' : 'outline'}
              className={filter === 'pix_paid' ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-emerald-500 text-emerald-700'}
              data-testid="filter-pix-paid"
            >
              PIX pago
            </Button>
            {filter === 'pix_pending' && <PixReconcileButton onDone={() => loadSales(searchTerm)} />}
          </div>
        </div>

        <DateRangeFilter
          value={dateRange}
          onChange={setDateRange}
          onExportCsv={() => {
            downloadCsv(`vendas_${dateRange.start}_a_${dateRange.end}.csv`, sales, [
              { key: 'created_at', label: 'Data', format: (v) => v ? new Date(v).toLocaleString('pt-BR') : '' },
              { key: 'sale_number', label: 'Nº' },
              { key: 'customer_name', label: 'Cliente' },
              { key: 'cashier_name', label: 'Operador' },
              { key: 'payment_method', label: 'Forma' },
              { key: 'total', label: 'Total (R$)', format: (v) => Number(v || 0).toFixed(2).replace('.', ',') },
              { key: 'status', label: 'Status' },
            ]);
          }}
          exportLabel="Exportar CSV"
          className="mb-4"
        />

        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <form onSubmit={handleSaleNumberSearch} className="bg-white border-2 border-blue-200 rounded-md p-4 flex items-center gap-2 md:w-[360px]" data-testid="sale-number-form">
            <Hash size={18} className="text-blue-600" />
            <Input
              type="number"
              min="1"
              placeholder="Nº da venda"
              value={saleNumber}
              onChange={(e) => { setSaleNumber(e.target.value); setSaleNumberMsg(''); }}
              className="flex-1 font-bold"
              data-testid="sale-number-input"
            />
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="sale-number-btn">Localizar</Button>
            {saleNumber && (
              <Button type="button" variant="outline" onClick={() => { setSaleNumber(''); setSaleNumberMsg(''); loadSales(''); }} data-testid="sale-number-clear">Limpar</Button>
            )}
          </form>
          <form onSubmit={handleSearch} className="bg-white border border-slate-200 rounded-md p-4 flex items-center gap-2 flex-1">
            <Search size={18} className="text-slate-500" />
            <Input
              placeholder="Buscar por nome do cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={nav.onKeyDown}
              title="Enter busca · ↑ ↓ navegam nos resultados · Enter no destaque abre a venda"
              className="flex-1"
              data-testid="search-sale"
            />
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="search-sale-btn">Buscar</Button>
            {searchTerm && (
              <Button type="button" variant="outline" onClick={clearSearch}>Limpar</Button>
            )}
          </form>
        </div>
        {saleNumberMsg && <p className="text-sm text-red-600 -mt-4 mb-4" data-testid="sale-number-msg">{saleNumberMsg}</p>}

        {todaySummary && (
          <div className="bg-white rounded-md border border-slate-200 p-6 mb-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2 flex-wrap" data-testid="period-summary-title">
              <ShoppingBag size={24} />
              {dateRange.start === todayStr() && dateRange.end === todayStr()
                ? 'Resumo de Hoje'
                : dateRange.start === dateRange.end
                  ? `Resumo de ${fmtBr(dateRange.start)}`
                  : `Resumo do Período: ${fmtBr(dateRange.start)} a ${fmtBr(dateRange.end)}`}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-blue-700">Vendas</p>
                    <p className="text-3xl font-black text-blue-600 mt-1" data-testid="period-summary-count">{todaySummary.total_vendas}</p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-yellow-700">Total Vendido</p>
                    <p className="text-3xl font-black text-yellow-700 mt-1" data-testid="period-summary-total">R$ {todaySummary.total_geral.toFixed(2)}</p>
                  </div>
                </div>
                {todaySummary.by_payment && Object.keys(todaySummary.by_payment).length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Por Forma de Pagamento</p>
                    <div className="flex flex-wrap gap-2" data-testid="period-summary-by-payment">
                      {Object.entries(todaySummary.by_payment).map(([pm, total]) => (
                        <span key={pm} className="text-sm px-3 py-1 rounded-md bg-slate-100 border border-slate-200 text-slate-700" data-testid={`summary-pm-${pm}`}>
                          <span>{pm === 'crediario' ? 'Crediário' : pm === 'outros' ? 'Outros' : closingLabel(pm)}</span>: <b>R$ {Number(total).toFixed(2)}</b>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Por Categoria</p>
                <div className="space-y-2">
                  {Object.keys(todaySummary.by_category).length === 0 ? (
                    <p className="text-sm text-slate-500">Nenhuma venda registrada no período</p>
                  ) : (
                    Object.entries(todaySummary.by_category).map(([cat, info]) => (
                      <div key={cat} className={`flex items-center justify-between px-3 py-2 rounded-md border ${getCategoryColor(cat)}`}>
                        <div>
                          <span className="font-bold text-sm">{getCategoryLabel(cat)}</span>
                          <span className="text-xs ml-2 opacity-75">Qtd: {info.quantity}</span>
                        </div>
                        <span className="font-bold">R$ {info.total.toFixed(2)}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Nº</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Data</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Caixa</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Pagamento</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Cupom</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {sales.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-10 text-slate-400">Nenhuma venda encontrada</td></tr>
                )}
                {sales.map((sale, idx) => {
                  const isCancelled = sale.status === 'cancelled';
                  return (
                    <tr key={sale.id} {...nav.rowProps(idx, `transition-colors ${isCancelled ? 'bg-red-50' : 'hover:bg-slate-50'}`)}>
                      <td className="px-4 py-3 font-bold text-blue-700">#{sale.sale_number || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{new Date(sale.created_at).toLocaleString('pt-BR')}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{sale.cashier_name}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{sale.customer_name || '-'}</td>
                      <td className="px-4 py-3 text-sm capitalize">
                        {sale.payment_method.replace('_', ' ')}
                        {sale.on_credit && <span className="text-xs ml-1 bg-yellow-100 text-yellow-700 px-1 rounded">a prazo</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${sale.cupom_type === 'fiscal' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                          {sale.cupom_type === 'fiscal' ? 'Fiscal' : 'Não Fiscal'}
                        </span>
                        <NfceBadge nfce={sale.nfce} />
                        <NfceActions sale={sale} onChanged={() => loadSales()} />
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          <PixBadge sale={sale} />
                          <PixRecheckButton sale={sale} onChanged={() => loadSales(searchTerm)} />
                          <NfeBadge nfe={sale.nfe} />
                          <NfeActions sale={sale} onChanged={() => loadSales()} />
                        </div>
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${isCancelled ? 'line-through text-slate-400' : 'text-blue-600'}`}>
                        R$ {sale.total.toFixed(2)}
                      </td>
                      <td className="px-4 py-3">
                        {isCancelled
                          ? <span className="text-xs px-2 py-1 rounded bg-red-600 text-white font-bold uppercase tracking-wider">Cancelada</span>
                          : <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 font-medium">Confirmada</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-3">
                          <button
                            onClick={() => setViewSaleDetail(sale)}
                            className="text-emerald-600 hover:text-emerald-700 font-medium text-sm"
                            title="Ver itens da venda"
                            data-testid={`view-sale-${sale.id}`}
                          >
                            <Eye size={16} className="inline mr-1" /> Ver itens
                          </button>
                          <button
                            onClick={() => handleReprint(sale.id)}
                            className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                            title="Reimprimir cupom"
                            data-testid={`reprint-sale-${sale.id}`}
                          >
                            <Printer size={16} className="inline mr-1" /> Reimprimir
                          </button>
                          {!isCancelled && (
                            <button
                              onClick={() => setCancelling(sale.id)}
                              className="text-red-600 hover:text-red-700 font-medium text-sm"
                              data-testid={`cancel-sale-${sale.id}`}
                            >
                              <X size={16} className="inline mr-1" /> Cancelar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
          </TabsContent>

          <TabsContent value="product-report">
            <ProductSalesReport />
          </TabsContent>

          <TabsContent value="weight-report">
            <WeightSalesReport />
          </TabsContent>

          <TabsContent value="discounts">
            <DiscountsReport />
          </TabsContent>

          <TabsContent value="credit-report">
            <CreditSalesReport />
          </TabsContent>

          <TabsContent value="receipts-report">
            <ReceiptsByPeriodReport />
          </TabsContent>

          <TabsContent value="returns">
            <ReturnsContent />
          </TabsContent>
        </Tabs>
      </div>

      {cancelling && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" {...overlayClose(() => setCancelling(null))}>
          <div className="bg-white rounded-lg p-6 w-96" onClick={(e) => e.stopPropagation()} data-testid="cancel-modal">
            <h3 className="text-xl font-bold text-slate-900 mb-2">Cancelar Venda</h3>
            <p className="text-sm text-slate-600 mb-4">
              A venda será cancelada, itens devolvidos ao estoque e o valor estornado no fluxo de caixa.
            </p>
            <div className="space-y-2 mb-4">
              <Label>Motivo do Cancelamento *</Label>
              <Input
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Ex: Cliente desistiu, produto avariado..."
                data-testid="cancel-reason-input"
              />
            </div>
            {error && <Alert variant="destructive" className="mb-3"><AlertDescription>{error}</AlertDescription></Alert>}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setCancelling(null); setCancelReason(''); setError(''); }} className="flex-1">
                Voltar
              </Button>
              <Button onClick={handleCancel} disabled={loading} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-cancel">
                {loading ? 'Cancelando...' : 'Confirmar Cancelamento'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {reprintSale && <ReceiptModal sale={reprintSale} onClose={() => setReprintSale(null)} />}

      {/* Modal: Ver itens da venda */}
      {viewSaleDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" {...overlayClose(() => setViewSaleDetail(null))}>
          <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="view-sale-detail">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-slate-900">Venda #{viewSaleDetail.sale_number}</h3>
              <button onClick={() => setViewSaleDetail(null)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><X size={18}/></button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm bg-slate-50 p-3 rounded mb-3">
              <div><strong>Data/Hora:</strong> {new Date(viewSaleDetail.created_at).toLocaleString('pt-BR')}</div>
              <div><strong>Operador:</strong> {viewSaleDetail.cashier_name || '—'}</div>
              <div><strong>Cliente:</strong> {viewSaleDetail.customer_name || '—'}</div>
              <div><strong>Pagamento:</strong> <span className="capitalize">{viewSaleDetail.payment_method?.replace('_',' ')}</span></div>
              <div><strong>Tipo:</strong> {viewSaleDetail.cupom_type === 'fiscal' ? 'Fiscal' : 'Não Fiscal'}</div>
              <div><strong>Status:</strong> {viewSaleDetail.status === 'cancelled' ? <span className="text-red-700 font-bold">CANCELADA</span> : 'Confirmada'}</div>
            </div>
            <div className="border-t pt-2">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-bold">Itens vendidos</p>
              <div className="space-y-1">
                {(viewSaleDetail.items || []).map((it, i) => (
                  <div key={i} className={`flex justify-between text-sm py-1 border-b border-slate-100 ${it.cancelled ? 'line-through text-slate-400' : ''}`} data-testid={`detail-item-${i}`}>
                    <div className="flex-1">
                      <span className="font-medium">{it.product_name}</span>
                      <span className="text-xs text-slate-500 ml-2">({it.quantity}x R$ {Number(it.unit_price).toFixed(2)})</span>
                    </div>
                    <span className="font-bold ml-2">R$ {Number(it.subtotal).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
            {viewSaleDetail.discount > 0 && (
              <div className="flex justify-between text-sm mt-2 text-yellow-700">
                <span>Desconto:</span><span>- R$ {Number(viewSaleDetail.discount).toFixed(2)}</span>
              </div>
            )}
            <div className="border-t pt-2 mt-2 flex justify-between font-bold text-xl">
              <span>TOTAL</span>
              <span className="text-blue-700">R$ {Number(viewSaleDetail.total).toFixed(2)}</span>
            </div>
            {viewSaleDetail.cancelled_reason && (
              <div className="mt-3 bg-red-50 border border-red-200 p-2 rounded text-xs">
                <strong>Motivo do cancelamento:</strong> {viewSaleDetail.cancelled_reason}
              </div>
            )}
            <Button onClick={() => setViewSaleDetail(null)} className="w-full mt-4">Fechar</Button>
          </div>
        </div>
      )}
    </Layout>
  );
};
